import { computed } from 'vue'
import BpmOsgpoModule from '@/helpers/Modules/Product/BpmOsgpoModule'

export function useBpmWizard ({
  policy,
  mode,
  notify,
  scoringLoading,
  changeDisabled,
  sharedApi,
}) {
  const wizardStep = computed({
    get () {
      const step = Number(policy?.wizardStep)
      return Number.isFinite(step) ? step : 0
    },
    set (value) {
      policy.wizardStep = value
    },
  })

  const wizardSteps = computed(() => BpmOsgpoModule.Wizard.stepsDictionary())

  const wizardFooterHint = computed(() =>
    BpmOsgpoModule.Validator.getWizardNextStepHint(wizardStep.value, policy, mode.value),
  )

  const canCalculate = computed(() =>
    BpmOsgpoModule.Validator.canCalculate(policy, mode.value),
  )

  const canCheckout = computed(() =>
    BpmOsgpoModule.Validator.canCheckout(policy, mode.value),
  )

  const wizardFooterShowBack = computed(() => wizardStep.value > 0)

  const wizardFooterPrimaryAction = computed(() => {
    if (wizardStep.value < 2) {
      return {
        label: 'Дальше',
        disabled: !wizardFooterHint.value.ready || changeDisabled.value || scoringLoading.value,
        loading: scoringLoading.value,
      }
    }
    if (canCheckout.value) {
      return {
        label: mode.value.checkoutTitle,
        disabled: changeDisabled.value,
        loading: changeDisabled.value,
      }
    }
    return {
      label: mode.value.calcTitle,
      disabled: !canCalculate.value || changeDisabled.value,
      loading: changeDisabled.value,
    }
  })

  function isWizardStepReachable (index) {
    return index <= BpmOsgpoModule.Validator.getMaxReachableWizardStep(policy)
  }

  function advanceWizardStep (targetStep) {
    if (targetStep <= wizardStep.value) {
      wizardStep.value = targetStep
      return
    }

    if (wizardStep.value !== 0 || targetStep < 1) {
      wizardStep.value = targetStep
      return
    }

    sharedApi.checkScoring().then(passed => {
      if (passed) {
        wizardStep.value = targetStep
      }
    })
  }

  function goToNextWizardStep () {
    const hint = BpmOsgpoModule.Validator.getWizardNextStepHint(wizardStep.value, policy, mode.value)
    if (!hint.ready) {
      if (hint.message) {
        notify.error({ message: hint.message })
      }
      return
    }
    advanceWizardStep(wizardStep.value + 1)
  }

  function goToPrevWizardStep () {
    if (changeDisabled.value || wizardStep.value <= 0) {
      return
    }
    wizardStep.value = wizardStep.value - 1
  }

  function onWizardStepChange (index) {
    if (index === wizardStep.value) {
      return
    }
    if (index < wizardStep.value) {
      if (!changeDisabled.value) {
        wizardStep.value = index
      }
      return
    }
    if (!isWizardStepReachable(index)) {
      const hint = BpmOsgpoModule.Validator.getWizardNextStepHint(wizardStep.value, policy, mode.value)
      if (hint.message) {
        notify.error({ message: hint.message })
      }
      return
    }
    advanceWizardStep(index)
  }

  function onWizardFooterPrimary () {
    if (wizardStep.value < 2) {
      goToNextWizardStep()
      return
    }
    if (canCheckout.value) {
      sharedApi.checkout()
      return
    }
    handleWizardCalculate()
  }

  function handleWizardCalculate () {
    if (!BpmOsgpoModule.Validator.canCalculate(policy, mode.value)) {
      const hint = BpmOsgpoModule.Validator.getWizardNextStepHint(2, policy, mode.value)
      if (hint.message) {
        notify.error({ message: hint.message })
      }
      return
    }
    sharedApi.calculate()
  }

  function syncWizardStepAfterPolicyChange () {
    const normalized = BpmOsgpoModule.Validator.normalizeWizardStep(wizardStep.value, policy)
    if (normalized !== wizardStep.value) {
      wizardStep.value = normalized
    }
  }

  function handlePolicyCriticalDataChanges () {
    policy.premium = null
    policy.calculationDate = null
    policy.premiumCalculation = null
    syncWizardStepAfterPolicyChange()
  }

  return {
    wizardStep,
    wizardSteps,
    wizardFooterHint,
    canCalculate,
    canCheckout,
    wizardFooterShowBack,
    wizardFooterPrimaryAction,
    isWizardStepReachable,
    advanceWizardStep,
    goToNextWizardStep,
    goToPrevWizardStep,
    onWizardStepChange,
    onWizardFooterPrimary,
    handleWizardCalculate,
    syncWizardStepAfterPolicyChange,
    handlePolicyCriticalDataChanges,
  }
}
